<template>
    <RFPRespondModal v-if="isRFPRespondActive" :isActive="isRFPRespondActive" :rfpRespond="rfpRespond"
        @close="isRFPRespondActive = false" />
    <div>
        <div class="proposals-header">
            <div class="proposals-tabs">
                <div class="tab" :class="activeTab === tab.value ? 'tab-active' : 'tab-inactive'" v-for="tab in tabs" :key="tab.value" @click="activeTab = tab.value">
                    {{ tab.label }}
                </div>
            </div>
        </div>
        <div v-if="getProposalsList().length > 0" class="proposals-list">
            <div v-for="proposal in getProposalsList()" :key="proposal.id" class="proposal-card" :class="proposal.status === 'withdrawn' ? 'proposal-card--withdrawn' : ''">
                <div class="proposal-card__content">
                    <div class="proposal-card__image">
                        <img :src="proposal.listing.preview_image" alt="Listing" class="proposal-image">
                    </div>
                    <div class="proposal-card__details">
                        <div class="proposal-header">
                            <div>
                                <a class="proposal-title" :href="`/item-detail/${proposal.listing.id}`">{{ proposal.listing.name }}</a>
                                <div class="proposal-description">{{ proposal.listing.short_description }}</div>
                            </div>
                            <div>
                                <div :class="getStatusBadge(proposal.status).class" class="status-badge">
                                    {{ getStatusBadge(proposal.status).label }}
                                </div>
                            </div>
                        </div>

                        <div class="proposal-info">
                            <div class="info-item">
                                <div class="info-item__label">Issuer</div>
                                <div class="info-item__value">{{ proposal.listing.applicant.username }}</div>
                            </div>
                            <div class="info-item">
                                <div class="info-item__label">Amount</div>
                                <div class="info-item__value">{{ formatPrice(proposal.price_lock_amount) }}</div>
                            </div>
                            <div class="info-item info-item--wide">
                                <div class="info-item__label">Created at</div>
                                <div class="info-item__value">{{ formatDate(proposal.created_at) }}</div>
                            </div>
                            <div class="info-item info-item--wide">
                                <div class="info-item__label">Due By</div>
                                <div class="info-item__value">{{ formatDate(proposal.listing.expire_at) }}</div>
                            </div>
                            
                        </div>
                    </div>
                </div>
                <div class="proposal-card__sidebar">
                    <div>
                        <div class="sidebar-title">Proposal</div>
                        <div class="sidebar-description">{{ InitialProposalText(proposal) }}</div>
                    </div>
                    <div class="sidebar-info">
                        <div>
                            <div class="sidebar-info__label">Applicant</div>
                            <div class="sidebar-info__value">{{ proposal.proponent.username }}</div>
                        </div>
                        <div>
                            <div class="sidebar-info__label">Created at</div>
                            <div class="sidebar-info__value">{{ formatDate(proposal.created_at) }}</div>
                        </div>
                    </div>
                    <div>
                        <button v-if="needMakeRequest(proposal)" 
                            :class="makedRequestToRFP(proposal) ? 'respond-to-rfp-btn--disabled' : 'respond-to-rfp-btn'" 
                            @click="toggleRFPRespondModal(proposal)">
                                {{ makedRequestToRFP(proposal) ? 'Responded' : 'Respond to RFP' }}
                        </button>
                        <div v-if="proposal.status !== 'withdrawn' && !needMakeRequest(proposal)" class="flex gap-2">
                            <button :class="proposal.status === 'withdrawn' || proposal.status === 'expired' ? 'view-proposal-btn--disabled' : 'view-proposal-btn'"
                                :disabled="proposal.status === 'expired'"
                                @click="handleViewProposal(proposal.id)">
                                <i class="uil uil-lightbulb"></i>
                                    View Proposal
                            </button>
                            <button class="respond-to-rfp-btn" v-if="hasResponseToRFP(proposal)"
                                :class="proposal.status === 'withdrawn' || proposal.status === 'expired' ? 'view-proposal-btn--disabled' : ''" 
                                @click="handleViewResponse(proposal)">
                                <i class="uil uil-lightbulb"></i>
                                    View Response
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else>
            <h1>No proposals found</h1>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import RFPRespondModal from '@/components/modals/RFPRespondModal.vue';
import { useStorage } from "@/composables/use-storage";
import { fetchMyProposals, fetchReviewProposals} from '@/services/api';
import { useRouter } from 'vue-router';
const router = useRouter();
const {  hasPermission, userInfo } = useStorage();

const tabs = ref([]);
const activeTab = ref('');
const myProposals = ref([]);
const reviewProposals = ref([]);
const isRFPRespondActive = ref(false);
const rfpRespond = ref(null);
const needMakeRequest = (proposal) => {
    return proposal.status === 'approved' && proposal.proponent.id === userInfo.value.id;
}

const hasResponseToRFP = (proposal) => {
    return proposal.listing.rfpRequests.length > 0 && proposal.proponent.id !== userInfo.value.id;
}

const makedRequestToRFP = (proposal) => {
    return proposal.listing.rfpRequests.length > 0 && proposal.proponent.id === userInfo.value.id;
}

const getProposalsList = () => {
    return activeTab.value === 'my-proposals' ? myProposals.value : reviewProposals.value;
}

const formatPrice = (price) => {
    const numPrice = parseFloat(price);
    return numPrice.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2, style: 'currency', currency: 'USD' });
}

const formatDate = (date) => {
    if(!date) return 'Does not expire';
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const InitialProposalText = (proposal) => {
    const textCleaned = removeMDImagesTags(proposal.message);
    return textCleaned.length > 70 ? textCleaned.substring(0, 70) + '...' : textCleaned;
}

const removeMDImagesTags = (text) => {
    return text.replace(/!\[Image\]\(.*?\)/g, '');
}

const getStatusBadge = (status) => {
    // enum('pending','approved','rejected','withdrawn','expired','blocked')
    const statuses = {
        pending: {
            label: 'Reviewing',
            class: 'bg-amber-500/20 text-amber-500 border-amber-500'
        },
        approved: {
            label: 'Approved',
            class: 'bg-green-600/20 text-green-600 border-green-600'
        },
        rejected: {
            label: 'Rejected',
            class: 'bg-red-600/20 text-red-600 border-red-600'
        },
        withdrawn: {
            label: 'Withdrawn',
            class: 'bg-gray-600/20 text-gray-400 border-gray-600'
        },
        expired: {
            label: 'Expired',
            class: 'bg-gray-600/20 text-gray-400 border-gray-600'
        },
        blocked: {
            label: 'Blocked',
            class: 'bg-gray-600/20 text-gray-400 border-gray-600'
        }
    }
    return statuses[status];
}

const handleViewProposal = (id) => {
    router.push({
        name: 'creator-profile',
        query: {
            page: 'new-proposal',
            proposalId: id
        }
    });
}

const toggleRFPRespondModal = (selected = null) => {
    if(makedRequestToRFP(selected)) {
        handleViewResponse(selected)
    }
    if (selected.status === 'withdrawn') return;
    isRFPRespondActive.value = !isRFPRespondActive.value
    rfpRespond.value = selected.listing
}

const handleViewResponse = (selected) => {
    const to = hasResponseToRFP(selected) ? 'rfp-requests' : 'rfp-responses';
    router.push({
        name: 'creator-profile',
        query: {
            page: to,
            id: selected.listing.rfpRequests[0].id
        }
    });
}

onMounted(async () => {
    if(hasPermission('datasets')) {
        myProposals.value = await fetchMyProposals();
        tabs.value.push({
            label: 'My Proposals',
            value: 'my-proposals'
        })
    }
    if(hasPermission('rfp-create')) {
        reviewProposals.value = await fetchReviewProposals();
        tabs.value.push({
            label: 'Review Proposals',
            value: 'review-proposals'
        })
    }
    activeTab.value = tabs.value[0].value;
})
</script>

<style scoped lang="postcss">
.proposals-header {
    @apply flex gap-2;
}

.proposals-tabs {
    @apply flex gap-2 border border-echoes-background-gray rounded-lg p-1;
}

.tab {
    @apply px-4 py-2 text-xs sm:text-sm rounded-lg w-full sm:w-fit cursor-pointer transition-all duration-300
}

.tab-inactive {
    @apply bg-echoes-background-black hover:bg-echoes-background-gray;
}

.tab-active {
    @apply bg-echoes-background-gray text-echoes-primary;
}

.proposals-list {
    @apply mt-4 gap-4 flex flex-col;
}

.proposal-card {
    @apply bg-echoes-background-gray w-full px-2 rounded-lg grid grid-cols-5 gap-2 divide-x divide-white/20 opacity-100;
}

.proposal-card--withdrawn {
    @apply opacity-60;
}

.proposal-card__content {
    @apply col-span-3 py-3 grid grid-cols-5 gap-4;
}

.proposal-card__image {
    @apply col-span-1;
}

.proposal-image {
    @apply w-full h-full object-cover rounded-lg;
}

.proposal-card__details {
    @apply col-span-4 flex flex-col gap-10;
}

.proposal-header {
    @apply flex justify-between;
}

.proposal-title {
    @apply font-semibold hover:text-echoes-primary;
}

.proposal-description {
    @apply text-xs text-white/80 line-clamp-2;
}

.status-badge {
    @apply text-xs px-2 py-0.5 rounded-full border;
}

.proposal-info {
    @apply grid grid-cols-6 text-sm;
}

.info-item {
    @apply flex flex-col col-span-1;
}

.info-item--wide {
    @apply col-span-2;
}

.info-item__label {
    @apply font-semibold text-white/60 text-xs;
}

.info-item__value {
    @apply text-white text-sm;
}

.proposal-card__sidebar {
    @apply col-span-2 py-3 ps-2 flex flex-col gap-3;
}

.sidebar-title {
    @apply font-semibold;
}

.sidebar-description {
    @apply text-xs text-white/60 line-clamp-2;
}

.sidebar-info {
    @apply flex justify-between text-sm;
}

.sidebar-info__label {
    @apply font-semibold text-white/60 text-xs;
}

.sidebar-info__value {
    @apply text-white;
}

.view-proposal-btn {
    @apply bg-echoes-primary text-white py-1 rounded-md w-full text-sm hover:bg-echoes-primary/80 transition-colors duration-200 ease-in-out;
}

.view-proposal-btn--disabled {
    @apply bg-gray-600/20 text-gray-400 border-gray-600 py-1 rounded-md w-full text-sm cursor-not-allowed;
}

.respond-to-rfp-btn {
    @apply bg-gradient-to-r text-sm 2xl:text-base from-green-500 to-sky-800 text-white hover:from-green-600 hover:via-green-500 hover:to-sky-700 cursor-pointer py-1 rounded-md w-full text-sm hover:bg-echoes-primary/80 transition-colors duration-200 ease-in-out;
}

.respond-to-rfp-btn--disabled {
    @apply bg-gradient-to-r text-sm 2xl:text-base from-violet-500 to-violet-800 text-white hover:from-violet-600 hover:via-violet-500 hover:to-violet-700 cursor-pointer py-1 rounded-md w-full text-sm hover:bg-echoes-primary/80 transition-colors duration-200 ease-in-out;
}
</style>